/**
 * "vs session median" speed-delta series — the motorsport delta channel
 * (MoTeC variance, VBOX Delta-T, AiM Time Compare).
 *
 * In a high-speed braking zone the absolute-speed chart slopes ~1 km/h per
 * metre, so lap-to-lap differences of a few km/h are sub-pixel. Plotting the
 * residual against a per-sample session-median reference makes them the
 * dominant feature: a brake point moved 8 m becomes a ~8 km/h delta spike.
 * This module is the ONLY place the reference and delta math live — both
 * clients consume it (2026-07-13 zone delta chart spec).
 */
import {
  MIN_LAPS_FOR_CONSISTENCY,
  SPEED_OK_THRESHOLD_KMH,
} from '../config/consistencyScoring'
import {
  SPEED_DELTA_AXIS_FLOOR_KMH,
  SPEED_DELTA_AXIS_STEP_KMH,
} from '../config/chartDisplay'
import { median } from '../sessionAnalysis/zoneDisplay/referencePointConsistency/robustStats'

export const SPEED_DELTA_AXIS_LABEL = 'Δ Speed (km/h)'
export const SPEED_DELTA_ZERO_LABEL = 'median'
/** Caption while the delta metric is active — "session median", never "typical lap" (accurate at 3 laps and 20 alike). */
export const SPEED_DELTA_CAPTION =
  'Each line: km/h faster or slower than the session median (0)'

/**
 * ±guide lines on the delta chart, drawn at the Speed-curve row's per-bin
 * gate — by construction, a lap crossing the guide is the same event that
 * fails the row's bin. One knob (SPEED_OK_THRESHOLD_KMH) moves both.
 * Always inside the axis: speedDeltaAxisBound never shrinks below the
 * ±15 floor.
 */
export const SPEED_DELTA_GUIDE_KMH = SPEED_OK_THRESHOLD_KMH
export const SPEED_DELTA_GUIDE_LABELS = {
  above: `+${SPEED_DELTA_GUIDE_KMH}`,
  below: `-${SPEED_DELTA_GUIDE_KMH}`,
}
export const SPEED_DELTA_GUIDE_CAPTION = `Dashed guides: laps within ±${SPEED_DELTA_GUIDE_KMH} km/h of the median count as consistent`

/**
 * Symmetric y-axis bound for a delta chart: ±15 km/h floor, expanding in
 * 5 km/h steps. NEVER autoscales below the floor. Both clients derive their
 * y-domain [-bound, +bound] from this one function.
 */
export function speedDeltaAxisBound(maxAbsDelta: number): number {
  return Math.max(
    SPEED_DELTA_AXIS_FLOOR_KMH,
    Math.ceil(maxAbsDelta / SPEED_DELTA_AXIS_STEP_KMH) *
      SPEED_DELTA_AXIS_STEP_KMH
  )
}

/** A speed sample on a lap trace. Extra properties on richer point types are ignored. */
export interface SpeedSample {
  distance: number
  speed: number
}

/** Reference lookup: distance → session-median speed, or null where fewer than MIN_LAPS_FOR_CONSISTENCY laps cover the distance. */
export type SpeedReference = (distance: number) => number | null

/**
 * Per-sample session-median reference over the given laps' speed traces.
 *
 * For a query distance, each lap's speed is linearly interpolated onto that
 * distance (piecewise-linear over the lap's own samples; no extrapolation —
 * laps not covering the distance are skipped), then the MEDIAN of the
 * interpolated values is taken. Median, never mean: its 50% breakdown point
 * means one outlier lap moves the reference by zero, where a 5-lap mean is
 * dragged 1/5 of the outlier's deviation and falsely shifts every other
 * lap's delta. Each lap's samples must be sorted ascending by distance —
 * both client pipelines already emit them that way.
 */
export function buildMedianSpeedReference(
  laps: SpeedSample[][]
): SpeedReference {
  const traces = laps.filter((points) => points.length > 0)
  return (distance: number): number | null => {
    const values: number[] = []
    for (const points of traces) {
      const v = interpolateSpeedAt(points, distance)
      if (v !== null) values.push(v)
    }
    if (values.length < MIN_LAPS_FOR_CONSISTENCY) return null
    return median(values)
  }
}

/** Delta of one sample against the reference: `lap speed − reference(distance)`, or null where the reference is unavailable. */
export function speedDeltaAt(
  reference: SpeedReference,
  sample: SpeedSample
): number | null {
  const ref = reference(sample.distance)
  return ref === null ? null : sample.speed - ref
}

function interpolateSpeedAt(
  points: SpeedSample[],
  distance: number
): number | null {
  if (
    distance < points[0].distance ||
    distance > points[points.length - 1].distance
  ) {
    return null
  }
  // Binary search: greatest index whose sample distance <= query distance.
  let lo = 0
  let hi = points.length - 1
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1
    if (points[mid].distance <= distance) lo = mid
    else hi = mid - 1
  }
  const a = points[lo]
  if (a.distance === distance || lo === points.length - 1) return a.speed
  const b = points[lo + 1]
  const span = b.distance - a.distance
  if (span <= 0) return a.speed
  const t = (distance - a.distance) / span
  return a.speed + t * (b.speed - a.speed)
}
